import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { XCircle } from 'lucide-react';
import { REJECTION_TEMPLATES } from '../constants';
import { AdminInputDialog } from '@/components/shared/AdminInputDialog';

interface StoreRejectDialogProps {
    isOpen: boolean;
    materialTitle: string;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

export function StoreRejectDialog({ isOpen, materialTitle, onConfirm, onCancel }: StoreRejectDialogProps) {
    const [reason, setReason] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);

    const reset = useCallback(() => {
        setReason('');
        setSelectedTemplate(null);
    }, []);

    useEffect(() => {
        if (!isOpen) reset();
    }, [isOpen, reset]);

    const handleTemplateClick = (idx: number) => {
        if (selectedTemplate === idx) {
            setSelectedTemplate(null);
            setReason('');
        } else {
            setSelectedTemplate(idx);
            setReason(REJECTION_TEMPLATES[idx].reason);
        }
    };

    const handleClose = () => {
        reset();
        onCancel();
    };

    const beforeField = (
        <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Razones predefinidas
            </p>
            <div className="grid grid-cols-2 gap-2">
                {REJECTION_TEMPLATES.map((t, idx) => (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => handleTemplateClick(idx)}
                        className={cn(
                            'px-3 py-2 rounded-lg text-xs font-medium text-left transition-all border',
                            selectedTemplate === idx
                                ? 'border-amber-500/50 bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20'
                                : 'border-border/50 bg-muted/20 text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <AdminInputDialog
            open={isOpen}
            variant="amber"
            title="Rechazar material"
            description={
                <p className="text-xs text-muted-foreground truncate" title={materialTitle}>
                    &quot;{materialTitle}&quot;
                </p>
            }
            leadingIcon={<XCircle size={18} className="text-amber-400" />}
            beforeField={beforeField}
            fieldLabel="Razón del rechazo"
            placeholder="Describe la razón del rechazo (visible para el usuario)…"
            rows={3}
            value={reason}
            onValueChange={(v) => {
                setReason(v);
                setSelectedTemplate(null);
            }}
            allowEmpty
            confirmLabel="Rechazar material"
            confirmIcon={<XCircle size={14} />}
            onCancel={handleClose}
            onConfirm={(v) => {
                onConfirm(v);
                reset();
            }}
        />
    );
}
